import React from "react";
import styled from "styled-components";
import { DialogActions, DialogContent, DialogTitle } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { IWorkspaceResourceFolder } from "common/interfaces";
import { Button as BeautifulButton } from "js/Components/Button";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { BeautifulDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { themeColors } from "js/react/sharedStyles";
import { defaultText } from "js/textStyles";
import { Gap10 } from "../Gap";

const logger = getLogger();

const StyledDialogTitle = styled(DialogTitle)`
    &.MuiDialogTitle-root {
        padding: 0;
    }

    h2 {
        ${defaultText}
        font-size: 23px;
        font-weight: 600;
        text-align: left;
    }
`;

const StyledDialogContent = styled(DialogContent)`
    &.MuiDialogContent-root {
        padding: 0;
    }

    ${defaultText}
    color: rgba(0, 0, 0, 0.75);
    font-size: 16px;
    line-height: 1.5;
`;

const StyledDialogActions = styled(DialogActions)`
    &.MuiDialogActions-root {
        justify-content: space-between;
        padding: 0;
        margin: 0;
    }
`;

const ButtonStyles = `
    padding: 0;
`;

const CancelButtonStyles = styled(BeautifulButton)`
    &&& {
        ${ButtonStyles}
    }
`;

const StyledDeleteButton = styled(BeautifulButton)`
    &&& {
        color: ${themeColors.warning};
        text-transform: uppercase;
        ${ButtonStyles}
    }
`;

const StyledBeautifulDialog = styled(BeautifulDialog)`
    &&& {
        .MuiDialog-paper {
            padding: 40px 50px ;
            gap: 10px;
            margin: 0;
        }
    }
`;

const DialogBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
`;

type DeleteWorkspaceResourceFolderDialogProps = {
    folder: IWorkspaceResourceFolder;
    closeDialog: (deleted?: boolean) => void;
};

export function DeleteWorkspaceResourceFolderDialog({ folder, closeDialog }: DeleteWorkspaceResourceFolderDialogProps) {
    const { t } = useTranslation();

    const { id, name } = folder;

    const handleDelete = async () => {
        try {
            await withProgressDialog({
                title: t("Deleting folder..."),
                action: () => WorkspaceController.deleteWorkspaceResourceFolder({ folderId: id }),
            });
            closeDialog(true);
        } catch (err) {
            logger.error(err, "[DeleteWorkspaceResourceFolderDialog] handleDelete failed", { folderId: id });
            ShowErrorDialog({
                title: t("Unable to delete folder"),
                message: (err as Error).message,
            });
        }
    };

    return (
        <StyledBeautifulDialog closeDialog={() => closeDialog(false)}>
            <DialogBody>
                <StyledDialogTitle>
                    {t('Delete "{{folderName}}"?', { folderName: name })}
                </StyledDialogTitle>
                <StyledDialogContent>
                    {t("Contents will be removed from folder, but not deleted.")}
                </StyledDialogContent>
            </DialogBody>
            <Gap10 />
            <StyledDialogActions>
                <CancelButtonStyles unframed large color={themeColors.mediumGray} onClick={() => closeDialog(false)}>
                    {t("Cancel")}
                </CancelButtonStyles>
                <StyledDeleteButton unframed large color={themeColors.warning} onClick={handleDelete}>
                    {t("Delete Folder")}
                </StyledDeleteButton>
            </StyledDialogActions>
        </StyledBeautifulDialog>
    );
}
